import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function parseDateTime(value?: string | Date | null): Date | null {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value !== "string") {
    return null;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  const date = new Date(trimmed);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatDateTime(
  value?: string | Date | null,
  fallback: string = "N/A",
  options?: Intl.DateTimeFormatOptions,
  locales?: Intl.LocalesArgument
): string {
  const date = parseDateTime(value);
  if (!date) {
    return fallback;
  }

  try {
    return date.toLocaleString(locales, options);
  } catch (error) {
    console.warn("[formatDateTime] Failed to format date:", error, value);
    return fallback;
  }
}

export function formatNumber(
  value: number | null | undefined,
  fallback: string = "0",
  locales?: Intl.LocalesArgument
): string {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return fallback;
  }

  try {
    return value.toLocaleString(locales);
  } catch (error) {
    console.warn("[formatNumber] Failed to format number:", error, value);
    return fallback;
  }
}

export function formatCurrency(
  value: number | null | undefined,
  {
    currencySymbol = "$",
    fallback = "0",
    locales,
  }: { currencySymbol?: string; fallback?: string; locales?: Intl.LocalesArgument } = {}
): string {
  const formatted = formatNumber(value, fallback, locales);
  return `${currencySymbol}${formatted}`;
}
